import React, { Suspense, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BillingProvider } from './contexts/BillingContext';
import { RecommendationProvider } from './contexts/RecommendationContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastContainer } from './components/Toast';
import AppSidebar from './components/AppSidebar';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import { useAnalytics } from './hooks/useAnalytics';
import ProtectedRoute from './components/ProtectedRoute';
import PostHogAuthSync from './components/PostHogAuthSync';
import { isPostHogEnabled } from './config/posthog';
import PreprocessingNotification from './components/PreprocessingNotification';
import AnalysisCompletionNotifier from './components/AnalysisCompletionNotifier';
import MobileDesktopBanner from './components/MobileDesktopBanner';
import { VideoAnalysisSupabaseService } from './services/videoAnalysisSupabaseService';
import { getExactRedirect, isAppPath, isMarketingPath } from './config/siteRoutes';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

// Lazy load page components for code splitting
const WelcomePage = React.lazy(() => import('./pages/WelcomePage'));
const OverviewPage = React.lazy(() => import('./pages/OverviewPage'));
const AnalysisPage = React.lazy(() => import('./pages/AnalysisPage'));
const VideoMetricsDashboardPage = React.lazy(() => import('./pages/VideoMetricsDashboardPage'));
const AnalysisHistoryPage = React.lazy(() => import('./pages/AnalysisHistoryPage'));
const RunStatusPage = React.lazy(() => import('./pages/RunStatusPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const RecommendationPage = React.lazy(() => import('./pages/RecommendationPage'));
const VisualInsightsPage = React.lazy(() => import('./pages/VisualInsightsPage'));
const UserCabinet = React.lazy(() => import('./components/UserCabinet'));
const PricingPage = React.lazy(() => import('./components/PricingPage'));
const ProductOverviewPage = React.lazy(() => import('./pages/ProductOverviewPage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const ResourcesPage = React.lazy(() => import('./pages/ResourcesPage'));
const MarketingPageShell = React.lazy(() => import('./components/marketing/MarketingPageShell'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PDFTestPage = React.lazy(() => import('./pages/PDFTestPage'));
const VideoAgentPage = React.lazy(() => import('./pages/VideoAgentPage'));
const ForensicAnalysisPage = React.lazy(() => import('./pages/ForensicAnalysisPage'));
const SocialScorePage = React.lazy(() => import('./pages/SocialScorePageV2'));
const ViralHooksResourcesPage = React.lazy(() => import('./pages/ViralHooksResourcesPage'));
const TikTokSlideshowsResourcesPage = React.lazy(() => import('./pages/TikTokSlideshowsResourcesPage'));
const NicheAnalyzerPage = React.lazy(() => import('./pages/NicheAnalyzerPage'));
const FreeToolsPage = React.lazy(() => import('./pages/FreeToolsPage'));
const FreeToolsPlatformPage = React.lazy(() => import('./pages/FreeToolsPlatformPage'));
const DownloaderRoutePage = React.lazy(() => import('./pages/DownloaderRoutePage'));
const BlogRoutePage = React.lazy(() => import('./pages/BlogRoutePage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Loading component for Suspense fallback
const PageLoader = (
  <div className="min-h-screen bg-gray-950 flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

/** App/authenticated routes should not compete in public SEO indexes. */
const PrivateAppSeo = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

// ✅ FIXED: Error boundary specifically for lazy component loading failures
interface LazyErrorBoundaryProps {
  children: React.ReactNode;
}

interface LazyErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class LazyErrorBoundary extends React.Component<LazyErrorBoundaryProps, LazyErrorBoundaryState> {
  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): LazyErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-red-500 mb-4">Failed to Load Page</h1>
            <p className="text-gray-400 mb-6">
              There was an error loading this page. This might be a temporary issue.
            </p>
            <button
              onClick={this.handleReload}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Reload Page
            </button>
            {this.state.error?.message && (
              <p className="text-sm text-red-300 mt-4 break-words">
                {this.state.error.message}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { setUserId, setUserProperties } = useAnalytics();
  
  // Set user ID for analytics when user is authenticated
  useEffect(() => {
    if (currentUser?.uid) {
      setUserId(currentUser.uid);
      setUserProperties({
        email: currentUser.email,
        display_name: currentUser.displayName,
      });
    }
  }, [currentUser, setUserId, setUserProperties]);

  const redirectTo = getExactRedirect(location.pathname);
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  const isMarketingPage = isMarketingPath(location.pathname);
  const isKnownAppPage = isAppPath(location.pathname);
  const showNavigation =
    isKnownAppPage && location.pathname !== '/payment-success' && location.pathname !== '/video-agent';
  const useSidebarLayout = showNavigation;

  if (!isMarketingPage && !isKnownAppPage) {
    return (
      <div className="min-h-screen bg-gray-950">
        <LazyErrorBoundary>
          <Suspense fallback={PageLoader}>
            <NotFoundPage />
          </Suspense>
        </LazyErrorBoundary>
      </div>
    );
  }

  // Public pages without sidebar (Welcome, Pricing, and other marketing routes)
  if (isMarketingPage) {
    return (
      <div className="min-h-screen bg-gray-950">
        <MobileDesktopBanner />
        <LazyErrorBoundary>
          <Suspense fallback={PageLoader}>
            <Routes>
              <Route path="/" element={<WelcomePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/product" element={<ProductOverviewPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/free-tools" element={<FreeToolsPage />} />
              <Route path="/free-tools/:platform" element={<FreeToolsPlatformPage />} />
              <Route
                path="/resources/viral-hooks"
                element={
                  <MarketingPageShell>
                    <ViralHooksResourcesPage />
                  </MarketingPageShell>
                }
              />
              <Route
                path="/resources/tiktok-slideshows"
                element={
                  <MarketingPageShell>
                    <TikTokSlideshowsResourcesPage />
                  </MarketingPageShell>
                }
              />
              <Route path="/resources/youtube-niche-analyzer" element={<NicheAnalyzerPage />} />
              <Route path="/resources/tiktok-niche-analyzer" element={<NicheAnalyzerPage />} />
              <Route path="/resources/instagram-niche-analyzer" element={<NicheAnalyzerPage />} />
              <Route path="/resources/facebook-niche-analyzer" element={<NicheAnalyzerPage />} />
              <Route path="/resources/x-niche-analyzer" element={<NicheAnalyzerPage />} />
              <Route path="/resources/analyze-instagram-reels" element={<Navigate to="/downloader/instagram-reel-downloader" replace />} />
              <Route path="/resources/unlock-your-videos-potential" element={<Navigate to="/blog/unlock-your-videos-potential" replace />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogRoutePage />} />
              <Route path="/downloader/:slug" element={<DownloaderRoutePage />} />
              <Route path="/video-agent-landing" element={<Navigate to="/video-agent" replace />} />
            </Routes>
          </Suspense>
        </LazyErrorBoundary>
      </div>
    );
  }

  // Protected pages without sidebar
  if (!useSidebarLayout) {
    return (
      <ProtectedRoute>
        <PrivateAppSeo />
        <div className="min-h-screen bg-gray-950">
          <MobileDesktopBanner />
          <LazyErrorBoundary>
            <Suspense fallback={PageLoader}>
              <Routes>
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                <Route path="/video-agent" element={<VideoAgentPage />} />
              </Routes>
            </Suspense>
          </LazyErrorBoundary>
        </div>
      </ProtectedRoute>
    );
  }

  // Pages with sidebar navigation - All require authentication
  return (
    <ProtectedRoute>
      <PrivateAppSeo />
      <SidebarProvider defaultOpen={true}>
        <div className="flex min-h-screen w-full bg-gray-950">
          <AppSidebar />
          <SidebarInset className="flex-1 min-w-0">
            <MobileDesktopBanner />
            <div className="sticky top-0 z-30 flex items-center gap-2 border-b border-sidebar-border bg-gray-950/90 px-3 py-2 backdrop-blur md:hidden">
              <SidebarTrigger className="text-white" />
              <span className="text-sm font-semibold text-white">Mentis AI</span>
            </div>
            <main id="pdf-export-content" className="flex-1 min-w-0 overflow-x-hidden">
              <LazyErrorBoundary>
                <Suspense fallback={PageLoader}>
                  <Routes>
                    <Route path="/overview" element={<OverviewPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/analysis/metrics" element={<VideoMetricsDashboardPage />} />
                    <Route path="/analysis" element={<AnalysisPage />} />
                    <Route path="/social-score" element={<SocialScorePage />} />
                    <Route path="/boost" element={<RecommendationPage />} />
                    <Route path="/insights" element={<VisualInsightsPage />} />
                    <Route path="/forensic" element={<ForensicAnalysisPage />} />
                    <Route path="/video-agent" element={<VideoAgentPage />} />
                    <Route path="/history" element={<AnalysisHistoryPage />} />
                    <Route path="/status" element={<RunStatusPage />} />
                    <Route path="/cabinet" element={<UserCabinet />} />
                    <Route path="/pdf-test" element={<PDFTestPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </LazyErrorBoundary>
            </main>
          </SidebarInset>
          
          {/* Global preprocessing notification - uses dynamic refresh (5s when active, 60s when idle) */}
          <PreprocessingNotification position="bottom-right" autoHide={false} />

          {/* Headless: toast when an analysis finishes (and user isn't on /status). */}
          <AnalysisCompletionNotifier />
        </div>
      </SidebarProvider>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {isPostHogEnabled() && <PostHogAuthSync />}
            <BillingProvider>
              <RecommendationProvider>
                <NotificationProvider>
                  <ToastProvider>
                    <ToastContainer />
                    <div className="App">
                      <AppContent />
                      {/* ✅ Initialize cache management on app load */}
                      <CacheInitializer />
                    </div>
                  </ToastProvider>
                </NotificationProvider>
              </RecommendationProvider>
            </BillingProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

// ✅ Component to initialize and manage cache lifecycle
const CacheInitializer: React.FC = () => {
  useEffect(() => {
    // Initialize cache cleanup on app mount
    VideoAnalysisSupabaseService.initializeCacheManagement();

    // Cleanup on app unmount
    return () => {
      VideoAnalysisSupabaseService.shutdownCacheManagement();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default App;
